import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { BASE_URL } from '../../insightConfig/insightConfig';
import { getCookie } from '../../commonServices/cookieService';
import YoutubeCustomVP from './YoutubeCustomVP';

const LiveClassesHome = () => {
    const [classes, setClasses] = useState([]);
    const [isDeviceRegistered, setIsDeviceRegistered] = useState(true);
    const [isAccessDenied, setIsAccessDenied] = useState(false); // ✅ NEW STATE for 409 errors
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedClass, setSelectedClass] = useState({});
    const [message, setMessage] = useState("");

    const onClose = () => {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        }
        setOpenModal(false);
        setSelectedClass({});
    }

    // 1. Fetch Classes (Read-Only Check)
    const fetchClasses = useCallback(async () => {
        try {
            const deviceId = getCookie("deviceId");
            // console.log("Fetching classes with DeviceID:", deviceId);

            const response = await axios.get(
                `${BASE_URL}/user/live-streams`,
                { params: { deviceId } }
            );

            // SUCCESS (200 OK)
            const fetchedClasses = response.data.data || [];
            // Sort classes by StartDate in ascending order (earliest first)
            const sortedClasses = fetchedClasses.sort((a, b) => {
                return new Date(a.StartDate) - new Date(b.StartDate);
            });
            setClasses(sortedClasses);
            setIsDeviceRegistered(true);
            setIsAccessDenied(false); // Reset access denied on success
            setMessage("");

        } catch (err) {
            console.error("Fetch Error Object:", err);

            // 1. Get the HTTP Status Code (Most Reliable)
            const httpStatus = err.response ? err.response.status : 0;
            const errMsg = err.response?.data?.message || "Unknown Error";

            console.log(`HTTP: ${httpStatus} | Message: ${errMsg}`);

            // SCENARIO A: Device Not Registered (403) -> Show Register Screen
            if (httpStatus == 403 || errMsg === 'DEVICE_NOT_REGISTERED') {
                setIsDeviceRegistered(false);
                setClasses([]);
                setMessage("This device is not registered.");
                setIsAccessDenied(false); 
            } 
            // SCENARIO B: Access Denied / Wrong Device (409) -> Show Table Error, Hide Logout
            else {
                setIsDeviceRegistered(true); // Keep table structure
                setClasses([]);
                setMessage(errMsg || "Error loading classes");
                setIsAccessDenied(true); // ✅ This hides the logout button
            }
        }
    }, []);

    // 2. Register Device (Action)
    const handleRegisterDevice = async () => {
        setLoading(true);
        try {
            const deviceId = getCookie("deviceId");
            await axios.post(`${BASE_URL}/user/register-device`, { deviceId });

            await fetchClasses();
            alert("Device registered successfully!");
        } catch (err) {
            alert(err.response?.data?.message || "Failed to register device");
        } finally {
            setLoading(false);
        }
    };

    // 3. Logout Device (Action)
    const renewDeviceId = useCallback(async () => {
        if (!window.confirm("Are you sure you want to logout from this device?")) return;

        try {
            const deviceId = getCookie("deviceId");
            await axios.get(
                `${BASE_URL}/user/remove-device-id`,
                { params: { deviceId } }
            );

            setIsDeviceRegistered(false);
            setClasses([]);
            setMessage("Device removed. Please register to view classes.");
        } catch (err) {
            console.error("Failed to remove device", err);
            alert(err.response?.data?.message || "Error logging out");
        }
    }, []);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    useEffect(() => {
        const now = Date.now();
        const upcoming = classes
            .map((c) => new Date(c.StartDate).getTime())
            .filter((time) => time > now);

        if (upcoming.length > 0) {
            const nextTime = Math.min(...upcoming);
            const timeout = nextTime - now;
            if (timeout < 2147483647) {
                const timer = setTimeout(() => {
                    fetchClasses();
                }, timeout + 5000);
                return () => clearTimeout(timer);
            }
        }
    }, [classes, fetchClasses]);

    const toLocal = (utcString) => new Date(utcString);

    const getVideoId = (url) => {
        if (!url) return "";
        const match = url.match(/(?:youtube\.com\/(?:.*v=|v\/|embed\/)|youtu\.be\/)([^"&?/ ]{11})/);
        return match ? match[1] : "";
    };

    const openVideoPlayer = (course) => {
        const id = getVideoId(course?.Link);
        if (!id) return alert("Invalid video link");

        const elem = document.documentElement;
        const requestFS = elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen;

        if (requestFS) {
            requestFS.call(elem).then(() => {
                setTimeout(() => {
                    setSelectedClass(course);
                    setOpenModal(true);
                }, 100);
            }).catch(err => {
                setSelectedClass(course);
                setOpenModal(true);
            });
        } else {
            setSelectedClass(course);
            setOpenModal(true);
        }
    }

    const isFuture = (dateString) => toLocal(dateString).getTime() > Date.now();

    const getMessage = (course) => {
        if (isFuture(course.StartDate)) {
            return <span className='text-info fw-medium ' >Upcoming...</span>
        } else {
            return <button
                className="btn btn-sm fw-bold " style={{ width: "100%", maxHeight: "100%", overflow: "hidden", background: 'transparent', backdropFilter: 'blur(0px)' }}
                onClick={() => openVideoPlayer(course)}
            >
                {course.Status == 2 ?
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="#ff0000" width="20" height="20">
                        <polygon className="play-btn__svg" points="9.33 6.69 9.33 19.39 19.3 13.04 9.33 6.69" />
                        <path className="play-btn__svg" d="M26,13A13,13,0,1,1,13,0,13,13,0,0,1,26,13ZM13,2.18A10.89,10.89,0,1,0,23.84,13.06,10.89,10.89,0,0,0,13,2.18Z" />
                    </svg>
                    :
                    <svg className='animate-blink' xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="32" height="32" viewBox="0 0 48 48">
                        <circle cx="24" cy="24" r="6" fill="#f44336"></circle><path fill="#f44336" d="M17.09,16.789L14.321,13.9C11.663,16.448,10,20.027,10,24s1.663,7.552,4.321,10.1l2.769-2.889 C15.19,29.389,14,26.833,14,24C14,21.167,15.19,18.61,17.09,16.789z"></path><path fill="#f44336" d="M33.679,13.9l-2.769,2.889C32.81,18.611,34,21.167,34,24c0,2.833-1.19,5.389-3.09,7.211l2.769,2.889 C36.337,31.552,38,27.973,38,24S36.337,16.448,33.679,13.9z"></path><g><path fill="#f44336" d="M11.561,11.021l-2.779-2.9C4.605,12.125,2,17.757,2,24s2.605,11.875,6.782,15.879l2.779-2.9 C8.142,33.701,6,29.1,6,24S8.142,14.299,11.561,11.021z"></path><path fill="#f44336" d="M39.218,8.121l-2.779,2.9C39.858,14.299,42,18.9,42,24s-2.142,9.701-5.561,12.979l2.779,2.9 C43.395,35.875,46,30.243,46,24S43.395,12.125,39.218,8.121z"></path></g>
                    </svg>
                }
            </button >
        }
    }

    return (
        <div>
            <div className="m-2">
                <div className="card History-card container">

                    {/* ✅ SWITCH VIEW BASED ON REGISTRATION STATUS */}
                    {!isDeviceRegistered ? (
                        <div className="text-center py-5">
                            <h4 className="text-danger mb-3">Access Restricted</h4>
                            <p className="text-muted mb-4 fs-5">
                                This device is not currently registered to your account.<br />
                                You must register this device to view live classes.
                            </p>
                            <button
                                className="btn btn-success px-5 py-2 fw-bold"
                                onClick={handleRegisterDevice}
                                disabled={loading}
                                style={{
                                    color: 'white',
                                    backgroundColor: '#198754',
                                    border: 'none',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                                onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                                onMouseOut={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Registering...
                                    </>
                                ) : (
                                    "Register This Device"
                                )}
                            </button>
                        </div>
                    ) : (
                        // ✅ Course Table Start
                        <div className="table-responsive" style={{ width: "100%" }}>
                            <table className="table" border="1">
                                <thead>
                                    <tr className="border-0">
                                        <th colSpan="6" className="p-0 border-0">
                                            {/* Main Header Container */}
                                            <div
                                                className="History1-course-heading d-flex justify-content-between align-items-center px-4 py-3"
                                                style={{
                                                    background: 'linear-gradient(90deg, #0d6efd 0%, #0a58ca 100%)',
                                                    color: 'black'
                                                }}
                                            >

                                                {/* 1. Empty Spacer (Keeps the title perfectly centered) */}
                                                <div style={{ width: '160px' }}></div>

                                                {/* 2. Title */}
                                                <h4 className="m-0 fw-bold text-uppercase" style={{ letterSpacing: '1px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                                    Live Class Details
                                                </h4>

                                                {/* 3. Logout Button OR Empty Spacer if Access Denied */}
                                                {!isAccessDenied ? (
                                                    <button
                                                        onClick={renewDeviceId}
                                                        className="btn btn-light btn-sm fw-bold text-primary shadow-sm"
                                                        style={{
                                                            width: '160px',
                                                            borderRadius: '30px',
                                                            transition: 'transform 0.2s'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                                                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                    >
                                                        Logout Device
                                                    </button>
                                                ) : (
                                                    // This spacer keeps the title centered even when button is gone
                                                    <div style={{ width: '160px' }}></div>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>

                                <thead>
                                    <tr>
                                        <th className="History2-course-heading table-border heading-center">S. N.</th>
                                        <th className="History2-course-heading table-border heading-center">Class Name</th>
                                        <th className="History2-course-heading table-border heading-center">Date</th>
                                        <th className="History2-course-heading table-border heading-center">Time</th>
                                        <th className="History2-course-heading table-border heading-center">Status</th>
                                        <th className="History2-course-heading table-border heading-center">Click To Join</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {classes?.length > 0 ? (
                                        classes.map((course, index) => {
                                            const localDate = toLocal(course.StartDate);
                                            return (
                                                <tr key={index}>
                                                    <td className="table-border fw-bold text-center">{index + 1}.</td>
                                                    <td className="table-border text-center">{course.Title}</td>
                                                    <td className="table-border text-center">{localDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                    <td className="table-border text-center">{localDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                                                    <td className="table-border text-center">
                                                        {course.Status == 2 ? "Class Over" : isFuture(course.StartDate) ? "Upcoming" : "Ongoing"}</td>
                                                    <td style={{ maxHeight: '40px', overflow: 'hidden' }} className="table-border d-flex justify-content-center align-items-center">
                                                        {getMessage(course)}
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-3">
                                                {/* Show Red Error Message if Access Denied, else normal message */}
                                                <span className={isAccessDenied ? "text-danger fw-bold" : ""}>
                                                    {message || "No live classes available"}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <Modal animation={false} onHide={setOpenModal} show={openModal} size="xl" backdrop="static"
                keyboard={false} centered fullscreen="lg-down" enforceFocus={false}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="exampleModalLabel">{selectedClass.Title}</h5>
                        <button type="button" className="btn-close" onClick={() => setOpenModal(false)}></button>
                    </div>
                    <div className="modal-body p-0">
                        <div className="live-class-container">
                            <div className="video-section">
                                <YoutubeCustomVP onClose={onClose} selectedClass={selectedClass} videoId={getVideoId(selectedClass?.Link) || "FhUekn5lUTw"} />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default LiveClassesHome;